import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { take } from 'lodash';
import { MessageBar, MessageBarType, Spinner, SpinnerSize, Text } from 'office-ui-fabric-react';
import { FocusZone, FocusZoneDirection } from 'office-ui-fabric-react/lib/FocusZone';
import { List } from 'office-ui-fabric-react/lib/List';
import { Persona, PersonaSize } from 'office-ui-fabric-react/lib/Persona';
import Module from '../Module';
import ModuleContent from '../ModuleContent';
import ModuleHeader from '../ModuleHeader';
import TextButton from '../TextButton';
import styles from './ActivityLogList.style';
import { viewableInitiatedByName, viewablePersonaProps } from '../../utils/activityLogUtils';

const bulletPointsItem = (style, bulletPoint) => {
  if (!bulletPoint.title && bulletPoint.value) {
    return (
      <div>
        <Text as="p" variant="small">
          {bulletPoint.value}
        </Text>
      </div>
    );
  }
  return (
    <div>
      <Text title={bulletPoint.title} as="p" variant="small">
        <b className={style.bold}>{bulletPoint.title}:</b> {bulletPoint.value}
      </Text>
    </div>
  );
};
const ActivityLogItem = item => {
  const { areaMessage, actionMessage, actionMessageEnding, bulletPoints, initiatedBy, timestamp } =
    item;

  let formattedTime = '';
  if (timestamp) {
    const datestamp = new Date(timestamp);
    formattedTime = `${datestamp.toLocaleString('default', {
      month: 'long',
    })} ${datestamp.getDate()}, ${datestamp.getFullYear()}`;
  }

  return (
    <div className={styles.activityItem} data-is-focusable>
      <Persona
        {...viewablePersonaProps(initiatedBy)}
        size={PersonaSize.size28}
        className={styles.personaCoin}
        hidePersonaDetails
      />

      <div className={styles.itemContent}>
        <div>
          <Text as="h4" className={styles.itemName}>
            {viewableInitiatedByName(initiatedBy)}
            <span className={styles.actionMessage}> {actionMessage} </span>
            {areaMessage}
            {actionMessageEnding && (
              <span className={styles.actionMessage}> {actionMessageEnding}</span>
            )}
          </Text>
        </div>
        <div>
          {bulletPoints?.length > 0 &&
            bulletPoints.map(bulletPoint => bulletPointsItem(styles, bulletPoint))}
        </div>
      </div>
      <Text as="p" variant="small" className={styles.date}>
        {formattedTime}
      </Text>
    </div>
  );
};

const ActivityLogList = ({ apiError, formattedActivityLogs, isLoading, title }) => {
  const [count, setCount] = useState(3);

  return (
    <Module>
      <ModuleHeader
        extra={
          <Text aria-label="Expanded" role="alert" as="span" className={styles.activityShowing}>
            Showing{' '}
            {count < formattedActivityLogs.length ? (
              <span>{count}</span>
            ) : (
              <span>{formattedActivityLogs.length}</span>
            )}{' '}
            of <span>{formattedActivityLogs.length}</span>
          </Text>
        }
        className={styles.activityLogHeader}
      >
        {title}
      </ModuleHeader>
      <ModuleContent className={styles.activityContent}>
        {apiError && (
          <MessageBar
            isMultiline={false}
            messageBarType={MessageBarType.error}
            dismissButtonAriaLabel="Close"
          >
            {apiError.name}: Try reloading or contact support.
          </MessageBar>
        )}
        {isLoading && (
          <>
            <br />
            <Spinner size={SpinnerSize.medium} />
          </>
        )}
        {!isLoading && (
          <FocusZone direction={FocusZoneDirection.vertical}>
            <List
              items={take(formattedActivityLogs, count)}
              onRenderCell={ActivityLogItem}
              getKey={item => item._id}
            />
          </FocusZone>
        )}
        {!isLoading && formattedActivityLogs.length > 3 && formattedActivityLogs.length > count && (
          <div>
            <TextButton
              tabIndex="0"
              noPadding
              aria-label="Show More Activity"
              className={styles.showMoreActivityButton}
              onClick={() => setCount(count + 10)}
            >
              <Text as="p" variant="small" className={styles.showMoreActivity}>
                Show More Activity
              </Text>
            </TextButton>
          </div>
        )}
      </ModuleContent>
    </Module>
  );
};

ActivityLogList.propTypes = {
  apiError: PropTypes.object,
  formattedActivityLogs: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string.isRequired,
      areaMessage: PropTypes.string.isRequired,
      actionMessage: PropTypes.string.isRequired,
      actionMessageEnding: PropTypes.string,
      bulletPoints: PropTypes.arrayOf(
        PropTypes.shape({
          title: PropTypes.string.isRequired,
          value: PropTypes.string.isRequired,
        }),
      ),
      initiatedBy: PropTypes.shape({
        name: PropTypes.string.isRequired,
        email: PropTypes.string.isRequired,
      }),
      timestamp: PropTypes.string,
    }),
  ),
  isLoading: PropTypes.bool,
  title: PropTypes.string,
};

ActivityLogList.defaultProps = {
  apiError: null,
  formattedActivityLogs: [],
  isLoading: false,
  title: 'Activity',
};

export default ActivityLogList;
